import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, Departamento, DepartamentosPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchDepartamentos = (
  params?: GetDepartamentosParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['adminDepartamentos', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getDepartamentos(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetDepartamento = (id: string) => {
  return useQuery({
    queryKey: ['adminDepartamentos', id],
    queryFn: async () => {
      return getDepartamento(id);
    },
  });
};

export const useCreateDepartamento = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createDepartamento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDepartamentos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Departamento  creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateDepartamento = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateDepartamento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDepartamentos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Departamentos actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetDepartamentosParams = Partial<Departamento> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateDepartamentoParams = Omit<Departamento, 'id_departamento'>;
export type UpdateDepartamentoParams = {
  id: string;
  data: CreateDepartamentoParams;
};

export const getDepartamentos = async (params?: GetDepartamentosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<DepartamentosPagingRes>(
    `${urlBackend}api/v1/departamento/?${queryParams}`,
    true
  );
};

export const getDepartamento = async (id: string) => {
  try {
    return await get<Departamento>(
      `${urlBackend}api/v1/departamento/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createDepartamento = async (data: CreateDepartamentoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Departamento>(`${urlBackend}api/v1/departamento/`, data, true);
};

export const updateDepartamento = async ({
  id,
  data,
}: UpdateDepartamentoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Departamento>(`${urlBackend}api/v1/departamento/${id}/`, data, true);
};